import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { isIBAN } from 'validator';
import { updateAuthUser } from '../../actions/auth';
import { Spinner, Alert } from '../../services/notiflix.service';
import AccountSettingsService from '../../services/account-settings.service';
import { useSelector, useDispatch } from 'react-redux';
import { connect, disconnect } from '../../actions/wallet';
import VerificationFailed from '../../components/common/VerificationFailed';
import KycService from '../../services/kyc.service';
import BlockchainService from '../../services/blockchain.service';
import AuthService from '../../services/auth.service';
import ChangePassword from '../../components/common/ChangePassword';
import EsignaturePop from '../../components/common/EsignaturesPop';
import { Switch } from '@headlessui/react';
import TwoFactorAuth from '../../components/common/TwoFactorAuth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import UserProfileDetails from '../../components/common/profile/UserProfile';
import KycTermsCondition from '../../components/common/KycTermsCondition';
import {
  UploadIcon,
  GreaterThanSignIcon,
  PlanRightSignIcon,
  PlusSignIcon,
  RightSign,
  ClockRunningIcon,
  CrossSignIcon,
  DownloadSvgIcon,
} from '../../components/common/IconProvider';
import { formatWithEllipsis, validateImageFile } from '../../utils/Helper';
import ImageLoader from '../../components/common/ImageLoader';
import { useTranslation } from 'react-i18next';
import KycVerification from '../../components/KycVerification';
import configService from '../../services/config.service';
const Profile = () => {
  const [enabled, setEnabled] = useState(false);
  const dispatch = useDispatch();
  const { wallet } = useSelector((state) => state.wallet);
  const { user: _authUser } = useSelector((state) => state.auth);
  const [showModalVerificationStatus, setShowModalVerificationStatus] =
    useState(false);
  const [isKycConsent, setKycConsent] = useState(false);
  const [is2FA, set2FA] = useState(false);
  const [isChangePasswordPop, setChangePasswordPop] = useState(false);
  const [showKycModel, setShowKycModel] = useState(false);
  const [showPlatformAgreement, setPlatformAgreement] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [platformWallet, setPlatformWallet] = useState(null);
  const [kycFailedReasons, setKycFailedReasons] = useState([]);
  const [profileImages, setProfileImages] = useState({
    src: null,
    data: null,
  });
  const Navigate = useNavigate();
  const location = useLocation();
  let projectId;
  let quantity;
  let paymentMethod;
  let slug;
  if (location.state) {
    projectId = location.state.id;
    quantity = location.state.quantity;
    paymentMethod = location?.state?.paymentMethod;
    slug = location?.state?.slug;
  }
  useEffect(() => {
    getProfileDetails();
    if (userProfile?.kyc_attempt > 0) {
      KycStatusCheck();
    }
  }, []);

  const { t } = useTranslation(['profile', 'common', 'auth']);
  let validationShape = {
    email: Yup.string()
      .required(t('auth:email_required'))
      .email(t('email_invalid')),
    full_name: Yup.string()
      .required(t('auth:full_name_required'))
      .matches(/^[^\s]+(\s[^\s]+)+$/, t('auth:name_format_required'))
      .max(40),

    phone_number: Yup.string().test(
      'is-valid-phone-number',
      t('auth:phone_number_invalid'),
      (value) => !value || /^\+?\d+$/.test(value),
    ),
    countryId: Yup.number().required(t('country_required')).nullable(),
  };

  //legal_entity_identifier validation
  validationShape['legal_entity_identifier'] = Yup.lazy(() =>
    Yup.string().when('legal_entity_identifier', {
      is: (legal_entity_identifier) => legal_entity_identifier,
      then: () =>
        Yup.string()
          .transform((value, originalValue) =>
            originalValue ? originalValue.toUpperCase() : value,
          )
          .min(20, t('auth:legal_entity_id_min_length'))
          .max(20, t('auth:legal_entity_id_max_length')),
    }),
  );

  validationShape['commerce_number'] = Yup.lazy(() => {
    if (isBusiness) {
      return Yup.string()
        .typeError('Commerce number must be a number')
        .required('Commerce number is required')
        .min(2, 'Commerce number must be at least 2 digits')
        .max(100, 'Commerce number must be at most 100 digits')
        .matches(/^\d+$/, 'Commerce number must contain only numbers');
    } else {
      return Yup.number()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value,
        )
        .nullable();
    }
  });

  //postal code validation
  validationShape['postal_code'] = Yup.lazy(() =>
    Yup.string().when(['address'], {
      is: (address) => address,
      then: () => Yup.string().required(t('auth:postal_code_required')),
    }),
  );

  //business name validation
  validationShape['business_name'] = Yup.lazy(() => {
    if (isBusiness) {
      return Yup.string().required(t('auth:business_name_required'));
    } else {
      return Yup.string()
        .transform(() => '')
        .nullable();
    }
  });

  // city validation
  validationShape['city'] = Yup.lazy(() =>
    Yup.string()

      .max(30, t('auth:city_max_length'))
      .when(['address'], {
        is: (address) => address,
        then: () => Yup.string().required(t('auth:city_required')),
      }),
  );

  //address validation
  validationShape['address'] = Yup.lazy(() =>
    Yup.string()

      .max(100, t('auth:address_max_length'))
      .when(['city'], {
        is: (postalCode, city) => postalCode || city,
        then: () => Yup.string().required(t('auth:address_required')),
      }),
  );

  //account number validation
  validationShape['account_no'] = Yup.lazy(() =>
    Yup.string()
      .required(t('auth:account_number_required'))
      .transform((value, originalValue) =>
        originalValue ? originalValue.toUpperCase() : value,
      )
      .matches(/^[A-Z0-9]*$/, t('auth:acount_number_format'))
      .test('IBAN', t('auth:iban_invalid'), (val) => {
        if (val) {
          return isIBAN(val);
        }
        return true;
      }),
  );

  // account holder name validation
  validationShape['account_holder_name'] = Yup.lazy(() =>
    Yup.string()
      .required(t('auth:account_holder_name_required'))
      .matches(/^[A-Za-z ]*$/, t('auth:valid_account_holder_name_required'))
      .min(3, t('auth:account_holder_name_min_length'))
      .max(40, t('auth:account_holder_name_max_length')),
  );
  const validationSchema = Yup.object().shape(validationShape);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const isBusiness = watch('isBusiness');

  const getProfileDetails = async () => {
    Spinner.show();
    try {
      let resp = await AccountSettingsService.details();

      Spinner.hide();
      if (resp.status === 'failure') {
        Alert.error(resp.message);
        return false;
      }
      setUserProfile(resp?.data);

      if (resp?.data?.kycReports && resp?.data?.kycReports?.length > 0) {
        const rawReasons = resp.data.kycReports[0]?.reasons;

        try {
          const parsedReasons = rawReasons ? JSON.parse(rawReasons) : null;

          if (!parsedReasons) {
            setKycFailedReasons([]);
          } else {
            const moderationComment =
              parsedReasons.moderationComment ??
              parsedReasons.rejectLabels ??
              null;

            if (!moderationComment) {
              setKycFailedReasons([]);
            } else if (Array.isArray(moderationComment)) {
              setKycFailedReasons(moderationComment.slice(0, 3));
            } else if (typeof moderationComment === 'string') {
              const reasons = moderationComment
                .split('\n')
                .map((r) => r.trim())
                .filter((line) => line !== '')
                .slice(0, 3);
              setKycFailedReasons(reasons);
            } else {
              setKycFailedReasons([String(moderationComment)]);
            }
          }
        } catch (error) {
          Alert.error(error.message);
          setKycFailedReasons([]);
        }
      }

      setPlatformWallet(
        resp?.data &&
          resp?.data?.userWallets?.length > 0 &&
          resp?.data?.userWallets.find(
            (userWallet) => userWallet.type === 'platform',
          ),
      );
      setProfileImages({
        data: null,
        src: resp.data?.profile_picture ? resp.data?.profile_picture : '',
      });

      const savedValues = {
        full_name: resp?.data.full_name ? resp.data.full_name : '',
        email: resp.data?.email ? resp.data.email : '',
        isBusiness: resp.data?.is_business ? resp.data?.is_business : 0,
        business_name: resp.data?.business_name
          ? resp?.data?.business_name
          : '',
        commerce_number: resp.data?.commerce_number
          ? resp?.data?.commerce_number
          : '',
        phone_number: resp.data?.phone_number ? resp.data?.phone_number : '',
        legal_entity_identifier: resp.data?.legal_entity_identifier
          ? resp.data?.legal_entity_identifier.toUpperCase()
          : '',
        address: resp.data?.address ? resp.data.address : '',
        postal_code: resp.data?.postal_code ? resp.data.postal_code : '',
        city: resp.data?.city ? resp.data.city : '',
        countryId: resp.data?.country_id,
        account_holder_name: resp.data?.userBankDetail?.account_holder_name
          ? resp.data?.userBankDetail?.account_holder_name
          : '',
        account_no: resp.data?.userBankDetail?.account_no
          ? resp.data?.userBankDetail?.account_no.toUpperCase()
          : '',
      };
      setEnabled(resp.data?.is_two_factor_enabled === 1 ? true : false);
      reset(savedValues);
      let authUser = { ..._authUser };
      authUser['profilePicture'] = resp.data.profile_picture;
      authUser['userName'] = resp.data.full_name;
      authUser['bank_details'] = resp.data?.userBankDetail?.account_no
        ? true
        : false;
      authUser['phoneNumber'] = resp.data?.phone_number;
      authUser['kyc_status'] = resp.data?.kyc_status;
      authUser['platform_agreement_status'] =
        resp.data?.platformAgreements?.length > 0
          ? resp.data?.platformAgreements[0]?.agreement_status
          : 0;
      dispatch(updateAuthUser(authUser));
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
    }
  };
  const updateProfile = async (_input) => {
    try {
      Spinner.show();

      let formData = new FormData();

      formData.append('full_name', _input.full_name);
      formData.append('email', _input.email);
      formData.append('is_business', _input?.isBusiness ? 1 : 0);
      if (_input.countryId) {
        formData.append('country_id', _input.countryId);
      }
      if (_input?.isBusiness) {
        if (_input.business_name) {
          formData.append('business_name', _input.business_name);
        }
        if (_input.commerce_number) {
          formData.append('commerce_number', _input.commerce_number);
        }
        if (_input.legal_entity_identifier) {
          formData.append(
            'legal_entity_identifier',
            _input.legal_entity_identifier
              ? _input.legal_entity_identifier.toUpperCase()
              : '',
          );
        }
      } else {
        formData.append('business_name', '');
        formData.append('commerce_number', '');
        formData.append('legal_entity_identifier', '');
      }
      if (_input.phone_number) {
        formData.append('phone_number', _input.phone_number);
      }
      if (_input.address) {
        formData.append('address', _input.address);
      }
      if (_input.account_no) {
        formData.append('account_no', _input.account_no);
      }
      if (_input.postal_code) {
        formData.append('postal_code', _input.postal_code);
      }
      if (_input.city) {
        formData.append('city', _input.city);
      }
      if (_input.account_holder_name) {
        formData.append('account_holder_name', _input.account_holder_name);
      }

      if (profileImages.data !== null && profileImages.data !== '') {
        formData.append('profile_picture', profileImages.data);
      }

      let resp = await AccountSettingsService.update(formData);

      if (resp.status === 'failure') {
        Spinner.hide();
        Alert.error(resp.message);
        return false;
      }
      await getProfileDetails();
      Spinner.hide();
      Alert.success(t('auth:profile_updated_successfully'));

      if (projectId && slug === 'contactUs') {
        Navigate(`/assets/details/${projectId}`, {
          state: {
            openContactPopup: true,
            quantity,
            paymentMethod,
          },
        });
      } else if (projectId && slug === 'bankDetail') {
        Navigate(`/assets/details/${projectId}`, {
          state: { openKycModal: true, quantity: quantity },
        });
      }
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };

  const modelClose = () => {
    setShowKycModel(false);
    setPlatformAgreement(false);
    getProfileDetails();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    // Validate the image file
    const { valid, error } = validateImageFile(file);
    if (!valid) {
      Alert.error(error);
      return;
    }

    setProfileImages({
      data: e.target.files[0],
      src: URL.createObjectURL(e.target.files[0]),
    });
  };

  const KYCModelOpen = () => {
    if (_authUser.is_kyc_consent === false) {
      setKycConsent(true);
      return false;
    }
    if (userProfile?.totalKycAttempt >= 2) {
      Alert.error(t('common:kyc_attempt_reached'));
      return false;
    }
    setShowKycModel(true);
  };
  const kycRejectPop = () => {
    if (userProfile?.totalKycAttempt >= 2) {
      return false;
    }
    setShowModalVerificationStatus(false);

    setShowKycModel(true);
  };

  const KycStatusCheck = async () => {
    try {
      let kycReport = await KycService.getKycReport();
      if (kycReport.status === 'failure') {
        Alert.error(kycReport.message);
        return false;
      }

      kycReport.data.forEach(() => {});
    } catch (error) {
      Alert.error(error.message);
      return false;
    }
  };
  useEffect(() => {
    getProfileDetails();
  }, [wallet]);

  const connectWallet = async () => {
    try {
      Spinner.show();
      let walletResp = await BlockchainService.connectWallet();
      if (walletResp.status === 'failure') {
        Spinner.hide();
        Alert.error(walletResp.message);
        return false;
      }

      Spinner.hide();
      let formData = {
        walletAddress: walletResp.data.walletAddress,
      };

      dispatch(connect(formData));

      const wallets = {
        wallet_address: walletResp.data.walletAddress,
        is_whitelisted: 0,
      };

      let res = await AccountSettingsService.addUserWallets(wallets);

      if (res.status === 'failure') {
        Spinner.hide();
        Alert.error(res.message);
        dispatch(disconnect());
        return false;
      }

      Alert.success(t('wallet_connected_successfully'));
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };

  const createPassword = async () => {
    try {
      if (_authUser.isChangePassword) {
        setChangePasswordPop(true);
        return;
      }
      Spinner.show();
      let resp = await AuthService.createPasswordEmail();
      if (resp.status === 'failure') {
        Spinner.hide();
        Alert.error(resp.message);
        return false;
      }
      Spinner.hide();
      Alert.success(resp.message);
      getProfileDetails();
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };
  const declineModelClose = async () => {
    setShowKycModel(false);
    setPlatformAgreement(false);
  };

  return (
    <>
      <div className="sm:pl-5 md:pt-5 pb-5">
        <form onSubmit={handleSubmit(updateProfile)}>
          <div className="text-center my-10 md:my-20">
            <h1 className="md:text-6xl font-medium mb-2 md:mb-6 dark:text-slate-200">
              {t('complete_profile')}
            </h1>
            <p className="text-md md:text-xl font-semibold max-w-3xl mx-auto dark:text-slate-200">
              {t('info')}
            </p>
          </div>

          <div className="bg-brandGray dark:bg-slate-800 dark:text-slate-200 py-5 md:px-6 px-4 rounded-md">
            <div className="xl:grid xl:grid-cols-2 xl:gap-8 ">
              <ProfileImgUploadSection
                profileImages={profileImages}
                handleImageUpload={handleImageUpload}
              />

              <UserProfileDetails
                setValue={setValue}
                register={register}
                clearErrors={clearErrors}
                errors={errors}
                userProfile={userProfile}
                isBusiness={isBusiness}
                showMailField={true}
                countryId={watch('countryId')}
              />

              <div className="md:mb-0 mb-5">
                <div className="mb-6 md:mb-6">
                  <h4 className="font-medium">{t('common:bank_details')}</h4>
                  <div className="border-t border-[#B6BDC5] mt-1 md:mt-2 pt-1 mb-1 md:mb-2"></div>
                  <p className="text-md text-[#85919F]">{t('bank_account')}</p>
                </div>

                <div className="xl:grid xl:grid-cols-2 lg:mt-[60px]">
                  <div className="xl:col-span-2">
                    <div className="mb-6">
                      <p className="responsive-text">
                        {t('account_number_iban')}
                        <span className="common-error-text">*</span>
                      </p>
                      <input
                        placeholder={t('account_number')}
                        type="text"
                        name="account_no"
                        className="dark:bg-[#4c5972] dark:text-white"
                        {...register('account_no', { required: true })}
                      />
                      <div className="common-error-text">
                        {errors.account_no?.message}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="mb-6">
                      <p className="responsive-text">
                        {t('common:in_the_name_of')}
                        <span className="common-error-text">*</span>
                      </p>
                      <input
                        placeholder="Peter Smith"
                        type="text"
                        name="account_holder_name"
                        className="dark:bg-[#4c5972] dark:text-white"
                        {...register('account_holder_name', { required: true })}
                      />
                      <div className="common-error-text">
                        {errors.account_holder_name?.message}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-end col-span-2 md:mb-0 mb-4">
                <div className="flex gap-3">
                  <button type="submit" className="bg-GreenColor text-black">
                    {t('update_profile')}
                  </button>
                </div>
              </div>

              <PasswordSecuritySection
                createPassword={createPassword}
                authUser={_authUser}
                enabled={enabled}
                set2FA={set2FA}
              />

              <WalletAddressSection
                platformWallet={platformWallet}
                connectWallet={connectWallet}
                wallet={wallet}
                userProfile={userProfile}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 mt-6 xl:mt-0">
                <KycSection
                  userProfile={userProfile}
                  KYCModelOpen={KYCModelOpen}
                  setShowModalVerificationStatus={
                    setShowModalVerificationStatus
                  }
                />
                <AgreementSection
                  userProfile={userProfile}
                  setPlatformAgreement={setPlatformAgreement}
                />
              </div>
            </div>
          </div>
        </form>
        {showKycModel && <KycVerification modelClose={modelClose} />}
        {isKycConsent && _authUser.is_kyc_consent === false && (
          <KycTermsCondition
            modelClose={setKycConsent}
            setKycModal={setShowKycModel}
          />
        )}
        {showPlatformAgreement && (
          <EsignaturePop
            declineModelClose={declineModelClose}
            type={'platform'}
            modelClose={modelClose}
          />
        )}

        {showModalVerificationStatus ? (
          <VerificationFailed
            kycStatus={userProfile?.kyc_status}
            kycRejectPop={kycRejectPop}
            reasons={kycFailedReasons}
            setShowModalVerificationStatus={setShowModalVerificationStatus}
            kycLimit={userProfile?.totalKycAttempt}
          />
        ) : null}
        {isChangePasswordPop ? (
          <ChangePassword setChangePasswordPop={setChangePasswordPop} />
        ) : null}

        {is2FA && (
          <TwoFactorAuth
            profileData={userProfile}
            TwoFa={set2FA}
            getProfileDetails={getProfileDetails}
          />
        )}
      </div>
    </>
  );
};

export const ProfileImgUploadSection = ({
  profileImages,
  handleImageUpload,
}) => {
  const { t } = useTranslation(['profile']);
  return (
    <>
      <div className="col-span-2 md:mb-0 mb-5">
        <div className="flex items-center gap-6">
          <div className="shrink-0 mb-4">
            <ImageLoader
              className="h-24 w-24 object-cover rounded-full"
              src={
                profileImages.src
                  ? profileImages.src
                  : '/img/default-user-icon.png'
              }
              alt="Current profile"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/img/default-user-icon.png';
              }}
            />
          </div>
          <div>
            <label className="relative inline-block py-2 px-4 rounded-lg bg-brandBlue text-white dark:bg-slate-900 cursor-pointer">
              <div className="flex items-center font-semibold text-sm gap-2">
                <UploadIcon />
                <span className="sm:inline-block hidden">
                  {t('upload_profile_pic')}
                </span>
              </div>
              <input
                type="file"
                name="profile"
                onChange={(e) => {
                  handleImageUpload(e);
                }}
                className="absolute w-full h-full top-0 left-0 opacity-0 cursor-pointer"
              />
            </label>
            <p className="text-brandBlue text-xs mt-2">
              {t('file_format_error')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export const PasswordSecuritySection = ({
  createPassword,
  authUser,
  enabled,
  set2FA,
}) => {
  const { t } = useTranslation(['passwordSecurity', 'common', 'auth']);
  return (
    <>
      <div className="mb-6 md:mb-6 col-span-2">
        <h4 className="font-medium">{t('password_and_security')}</h4>
        <div className="border-t border-[#B6BDC5] mt-1 md:mt-2 pt-1 mb-1 md:mb-2"></div>
        <p className="text-base text-[#85919F]">
          {t('update_password_and_security')}
        </p>

        <div className="grid md:grid-cols-2 gap-5 mt-5 items-center">
          <div className="">
            <div className="md:flex items-center gap-x-5">
              <p
                onClick={() => createPassword()}
                className="text-sm cursor-pointer font-medium bg-white dark:bg-slate-500 px-5 py-3 rounded-md text-blue-700 dark:text-GreenColor inline-flex items-center dark:border-GreenColor md:mb-0 mb-2"
              >
                {' '}
                {authUser.isChangePassword
                  ? t('common:change_password')
                  : t('create_password')}
                <GreaterThanSignIcon />
              </p>
            </div>
          </div>

          <div className="">
            <div className="flex items-center">
              <p className="text-base md:text-md mr-3 font-semibold">
                {t('two_factor_authentication')}
              </p>
              <Switch
                checked={enabled}
                onChange={() => set2FA(true)}
                disabled={authUser.isChangePassword === false ? true : false}
                className={`${enabled ? 'bg-GreenColor' : 'bg-gray-400'}
                            p-0 relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
              >
                <span className="sr-only">{t('use_setting')}</span>
                <span
                  aria-hidden="true"
                  className={`${enabled ? 'translate-x-7' : 'translate-x-0'}
                              pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            {authUser.isChangePassword === false ? (
              <p className="mt-3 text-gray-600 dark:text-white">
                <span className="font-semibold text-black dark:text-white">
                  {t('note')}
                </span>{' '}
                {t('activate_2fa_message')}
              </p>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const AgreementSection = ({ userProfile, setPlatformAgreement }) => {
  const { t } = useTranslation('profile');
  return (
    <>
      <div>
        <div className="mb-6 md:mb-6">
          <h4 className="font-medium text-xl">{t('agreements')}</h4>
          <div className="border-t border-[#B6BDC5] mt-1 md:mt-2 pt-1 mb-1 md:mb-2"></div>
          <p className="text-base text-[#85919F]">&nbsp;</p>
        </div>

        <div className="col-span-6">
          <div className="mb-6">
            <p className="text-sm md:text-md mb-1">{t('platform_agreement')}</p>
            {userProfile?.platformAgreements?.length > 0 &&
            userProfile?.platformAgreements[0]?.agreement_status === 1 ? (
              <>
                <div className="relative pr-40 border-b border-gray-100 p-2 pb-2">
                  <div className="flex items-center gap-4 relative top-0">
                    {t('signed')}
                  </div>
                  <a
                    href={userProfile?.platformAgreements[0]?.agreement_url}
                    target="_blank"
                    className="absolute right-2 top-[6px] flex gap-2 items-center bg-GreenColor text-brandBlue px-2 py-1 rounded-sm text-sm"
                    rel="noreferrer"
                  >
                    <DownloadSvgIcon /> {t('view')}
                  </a>
                </div>
              </>
            ) : userProfile?.platformAgreements.agreement_status === 2 ? (
              <div className="col-span-6">
                <div className="mb-6">
                  <div className="relative pr-40 border-b border-gray-100 p-2 pb-2">
                    <div className="flex items-center gap-4 relative top-0">
                      {t('agreement_declined')}
                    </div>
                    <Link
                      onClick={() => setPlatformAgreement(true)}
                      className="absolute right-2 top-[6px] flex gap-2 items-center bg-GreenColor text-brandBlue px-2 py-1 rounded-sm text-sm"
                    >
                      <DownloadSvgIcon /> {t('sign')}
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-span-6">
                <div className="mb-6">
                  <div className="relative pr-40 border-b border-gray-100 p-2 pb-2">
                    <div className="flex items-center gap-4 relative top-0">
                      {t('common:pending')}
                    </div>
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        if (
                          userProfile?.userBankDetail !== null &&
                          userProfile?.kyc_status === 1
                        ) {
                          setPlatformAgreement(true);
                        }
                      }}
                      className={`absolute right-2 top-[6px] flex gap-2 items-center px-2 py-1 rounded-sm text-sm ${
                        userProfile?.userBankDetail === null ||
                        userProfile?.kyc_status !== 1
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-GreenColor text-brandBlue'
                      }`}
                    >
                      <DownloadSvgIcon /> {t('sign')}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col-span-6">
          <div className="mb-6">
            {userProfile?.projectAgreements?.length > 0 ? (
              <>
                {userProfile.projectAgreements
                  .filter((value) => {
                    return value.agreement_status === 1 && value.agreement_url;
                  })
                  .map((value) => (
                    <div
                      key={value.id}
                      className="relative pr-40 border-b border-gray-100 p-2 pb-2"
                    >
                      <div className="flex items-center gap-4 relative top-0">
                        <p
                          title={
                            value?.projectDetail
                              ? value?.projectDetail?.name
                              : 'NA'
                          }
                          className="truncate"
                        >
                          {formatWithEllipsis(
                            value?.projectDetail?.name || '-',
                            10,
                          )}
                        </p>
                      </div>
                      <a
                        href={value?.agreement_url}
                        target="_blank"
                        className="absolute right-2 top-[6px] flex gap-2 items-center bg-GreenColor text-brandBlue px-2 py-1 rounded-sm text-sm"
                        rel="noreferrer"
                      >
                        <DownloadSvgIcon /> {t('view')}
                      </a>
                    </div>
                  ))}
              </>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const WalletAddressSection = ({
  platformWallet,
  wallet,
  userProfile,
  connectWallet,
}) => {
  const { t } = useTranslation('profile');
  return (
    <>
      <div className="md:mb-0 mb-5">
        <div className="mb-6 md:mb-6">
          <h4 className="font-medium text-xl">{t('wallet_address')}</h4>
          <div className="border-t border-[#B6BDC5] mt-1 md:mt-2 pt-1 mb-1 md:mb-2"></div>
          <p className="text-base text-[#85919F]">
            {t('choose_primary_wallet_added')}
          </p>
        </div>

        <div className="p-3 border border-dashed border-gray-300 rounded-2xl">
          <div className="w-full bg-white dark:bg-slate-600 p-4 mb-3 rounded-xl">
            <h5 className="text-lg font-medium">
              {t('wallet', { walletName: configService.appName() })}
            </h5>

            {platformWallet ? (
              <>
                <p className="text-gray-600 mb-2">{t('platform_created')}</p>

                <div className="relative mb-3">
                  <p className="py-4 px-4 rounded-lg border border-gray-200 text-sm text-black dark:text-white">
                    {platformWallet?.wallet_address || 'NA'}
                  </p>

                  {!wallet && (
                    <span className="w-6 h-6 rounded-full border border-GreenColor flex items-center justify-center p-1 bg-GreenColor absolute top-1/2 right-3 -translate-y-1/2">
                      <PlanRightSignIcon />
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-2">
                  {t('platform_created_after_purchase')}
                </p>
                <div className="relative mb-3">
                  <p className="py-4 px-4 rounded-lg border border-gray-200 text-sm text-black dark:text-white">
                    {t('yet_to_be_created')}
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="w-full bg-white p-4 mb-3 rounded-xl dark:bg-slate-600">
            <div className="flex items-center justify-between">
              <h5 className="text-lg font-medium">{t('personal_wallets')} </h5>
              <button
                type="button"
                className="flex items-center text-sm font-medium text-blue-800 dark:text-white"
                onClick={() => connectWallet()}
              >
                <PlusSignIcon />
                {t('add_wallet')}
              </button>
            </div>
            <p className="text-gray-600 mb-2">{t('wallet_description')}</p>
            {userProfile && userProfile?.userWallets?.length > 0 ? (
              userProfile?.userWallets.map(
                (userWallet, index) =>
                  userWallet.type === 'noncustodial' && (
                    <div key={index} className="relative mb-3">
                      <p className="py-4 px-4 rounded-lg border border-gray-200 text-sm text-black dark:text-white">
                        {userWallet?.wallet_address || 'NA'}
                      </p>

                      {wallet &&
                        userWallet?.wallet_address ===
                          wallet?.walletAddress && (
                          <span className="w-6 h-6 rounded-full border border-GreenColor flex items-center justify-center p-1 bg-GreenColor absolute top-1/2 right-3 -translate-y-1/2">
                            <PlanRightSignIcon />
                          </span>
                        )}
                    </div>
                  ),
              )
            ) : (
              <p className="py-4 px-4 rounded-lg border border-gray-200 text-sm text-black dark:text-white">
                {t('no_wallets_connected')}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const KycSection = ({
  userProfile,
  KYCModelOpen,
  setShowModalVerificationStatus,
}) => {
  const { t } = useTranslation(['profile', 'common']);
  return (
    <>
      <div>
        <div className="mb-6 md:mb-6">
          <h4 className="font-medium text-xl">KYC Status</h4>
          <div className="border-t border-[#B6BDC5] mt-1 md:mt-2 pt-1 mb-1 md:mb-2"></div>
          <p className="text-base text-[#85919F]">&nbsp;</p>
        </div>

        <div className="col-span-6">
          <div className="mb-6">
            {userProfile && userProfile?.kyc_status === 1 ? (
              <div className="inline-flex items-center text-green-700 text-sm font-semibold">
                <RightSign />
                {t('kyc_verified')}
              </div>
            ) : userProfile?.kyc_status === 0 ? (
              <div className="col-span-6">
                <div className="mb-6">
                  <div className="inline-flex items-center text-yellow-500 font-semibold">
                    <ClockRunningIcon /> {t('common:pending')}
                  </div>
                  <div className="block mt-2 cursor-pointer">
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        if (userProfile?.userBankDetail !== null) {
                          KYCModelOpen();
                        }
                      }}
                      className={`text-blue-600 border-b border-blue-600 dark:text-GreenColor dark:border-GreenColor pb-1 text-sm ${
                        userProfile?.userBankDetail === null
                          ? 'opacity-50 cursor-not-allowed dark:text-green-200 dark:border-green-200'
                          : ''
                      }`}
                    >
                      {t('common:proceed_to_kyc')}
                    </Link>
                  </div>
                </div>
              </div>
            ) : userProfile?.kyc_status === 3 ? (
              <div className="text-brandOrange font-semibold w-auto inline-flex items-center">
                <p className="leading-tight text-sm flex items-center gap-1">
                  <ClockRunningIcon />
                  {t('common:kyc_initaited')}
                </p>
                {/* Continue Card */}
                <p
                  className="text-blue-500 py-2 px-2 rounded w-auto inline-flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    if (userProfile?.userBankDetail !== null) {
                      KYCModelOpen();
                    }
                  }}
                >
                  <p className="leading-tight text-md font-semibold underline">
                    {t('common:continue')}
                  </p>
                </p>
              </div>
            ) : userProfile?.kyc_status === 4 ? (
              <>
                <div className="text-brandOrange font-semibold w-auto flex items-center">
                  {' '}
                  <ClockRunningIcon _bold={true} />
                  <p className="leading-tight text-sm  font-semibold">
                    {' '}
                    {t('common:in_review')}
                  </p>
                </div>

                <Link
                  onClick={() => setShowModalVerificationStatus(true)}
                  className=" mt-3 inline-block text-blue-600 dark:text-GreenColor border-b dark:border-GreenColor border-blue-600 pb-1 text-sm"
                >
                  {t('common:check_the_queries')}
                </Link>
              </>
            ) : (
              <div>
                <div className=" text-brandRed w-auto inline-flex items-center font-semibold">
                  {' '}
                  <CrossSignIcon />
                  <p className="leading-tight font-semibold text-sm">
                    {t('common:kyc_failed')}
                  </p>
                </div>

                <div className="block mt-2 cursor-pointer">
                  <Link
                    onClick={() => setShowModalVerificationStatus(true)}
                    className="text-blue-600 dark:text-GreenColor border-b dark:border-GreenColor border-blue-600 pb-1 text-sm"
                  >
                    {t('check_queries')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
